"use client"

import { useState, useMemo, useEffect, useRef, useCallback } from "react"
import { useBooruSearch } from "@/lib/booru-store"
import { useScriptsOnlyPosts } from "@/hooks/use-scripts-only-posts"
import { useScriptsOnlyStore } from "@/lib/scripts-only-store"
import type { BooruContentTypeVisibility, BooruPost, BooruSite } from "@/lib/booru-api"
import { getPostMediaTypes, isGifPost } from "@/lib/booru-media-types"
import { useBlacklistStore } from "@/lib/blacklist-store"
import { isAiPost } from "@/lib/ai-content"
import type { PostDateRange } from "@/lib/post-date"
import { useSettingsStore } from "@/lib/settings-store"
import { useSourceStore, sourceHasNoCredentials } from "@/lib/source-store"
import { useBooruCredentialsStore } from "@/lib/booru-credentials-store"
import { useNavigationStore } from "@/lib/navigation-store"
import { getNavigationMode } from "@/lib/navigation-mode"
import { BooruGrid } from "./booru-grid"
import { BooruAuthDialog } from "./booru-auth-dialog"
import { AlbumGridView } from "./album-grid-view"
import { BoardGridView } from "./board-grid-view"
import { BoardCards } from "./board-cards"
import { InfinitePagesViewer } from "./infinite-pages-viewer"
import { AudioChanFilterStrip, DEFAULT_AUDIOCHAN_FILTERS, type AudioChanFilters } from "./audiochan-filter-strip"
import { CivitaiMediaTypeTabs, type CivitaiMediaType } from "./civitai-media-type-tabs"
import { Rule34FilterTabs } from "./rule34-filter-tabs"
import { rule34FilterTokens } from "@/lib/rule34-filter-terms"
import { E621FilterTabs } from "./e621-filter-tabs"
import { YandereFilterTabs } from "./yandere-filter-tabs"
import { EpornerFilterTabs, type EpornerOrder, type EpornerOrientation } from "./eporner-filter-tabs"
import { XvideosFilterTabs } from "./xvideos-filter-tabs"
import { ArfreaksFilterTabs } from "./arfreaks-filter-tabs"
import { RedgifsFilterTabs } from "./redgifs-filter-tabs"
import { SexvrFilterTabs } from "./sexvr-filter-tabs"
import { VrhumpFilterTabs } from "./vrhump-filter-tabs"
import { SankakuFilterTabs } from "./sankaku-filter-tabs"
import { RealbooruFilterTabs } from "./realbooru-filter-tabs"
import { VrpornbaseFilterTabs } from "./vrpornbase-filter-tabs"
import type { YandereOrder } from "@/lib/yandere-filter-terms"
import { BEST_OF_SITES } from "@/lib/best-of-window"
import { E621_FAMILY, e621StripLabel, type E621Order } from "@/lib/e621-filter-terms"
import { EromeVersionTabs } from "./erome-version-tabs"
import { FourchanBoardTabs } from "./fourchan-board-tabs"
import { SourceFilterBar } from "./source-filter-bar"
import { HentaieraCategoryTabs, type HentaieraCategory } from "./hentaiera-category-tabs"
import { GoontaiGenreTabs } from "./goontai-genre-tabs"
import { HitomiFilterTabs, hitomiFilterTags, type HitomiLanguage, type HitomiType } from "./hitomi-filter-tabs"
import { LewdvaultSortTabs, type LewdvaultSort } from "./lewdvault-sort-tabs"
import { DoujinsCategoryTabs, type DoujinsCategory } from "./doujins-category-tabs"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { WelcomeSourcePicker } from "@/components/welcome-source-picker"
import { Loader2, RefreshCw, Info, Key, ChevronLeft, ChevronRight } from "@/lib/icons"
import { useTagFiltersStore } from "@/lib/tag-filters-store"
import { useUserRatingsStore } from "@/lib/user-ratings-store"
import { postHasExcludedTag } from "@/lib/negative-tag-filter"
import { ALL_FILTER_RATINGS, postMatchesRatings, type FilterRating } from "@/lib/rating-filter"
import { postHasShowableImage, summarizeHiddenByContentType, summarizeHiddenByRating } from "@/lib/post-visibility"
import { postInDateRange } from "@/lib/post-date"
import { useFavoritesStore } from "@/lib/favorites-store"
import { CloudflareVerificationPrompt } from "@/components/cloudflare-verifier"
import { useCloudflareVerificationStore } from "@/lib/cloudflare-verification-store"
import { useTranslations } from "next-intl"
import { RecommendationWidgetAd } from "@/components/ads/RecommendationWidgetAd"

// Calculate relevance score based on search tag matches
function calculateRelevanceScore(post: BooruPost, searchTags: string[]): number {
  if (!searchTags.length || !post.tags.length) return 0
  
  let score = 0
  const postTagsLower = post.tags.map(tag => tag.toLowerCase())
  
  for (const searchTag of searchTags) {
    const searchTagLower = searchTag.toLowerCase()
    
    // Exact match gets highest score
    if (postTagsLower.includes(searchTagLower)) {
      score += 10
      continue
    }
    
    // Partial matches and related tags
    for (const postTag of postTagsLower) {
      // Contains search term (e.g., "feet" matches "barefoot")
      if (postTag.includes(searchTagLower)) {
        score += 5
        continue
      }
      
      // Search term contains post tag (e.g., "barefoot" matches "foot")
      if (searchTagLower.includes(postTag) && postTag.length > 2) {
        score += 3
        continue
      }
      
      // Related tag patterns (customize based on common tag relationships)
      const relatedTags = getRelatedTags(searchTagLower)
      if (relatedTags.includes(postTag)) {
        score += 7
        continue
      }
    }
  }
  
  // Bonus for multiple search tag matches
  const exactMatches = searchTags.filter(searchTag => 
    postTagsLower.includes(searchTag.toLowerCase())
  ).length
  
  if (exactMatches > 1) {
    score += exactMatches * 5 // Bonus for multiple exact matches
  }
  
  return score
}

// Get related tags for better relevance matching
function getRelatedTags(searchTag: string): string[] {
  const relatedTagsMap: Record<string, string[]> = {
    // Feet-related tags
    'feet': ['barefoot', 'soles', 'toes', 'foot_fetish', 'feet_focus', 'foot_focus'],
    'foot': ['feet', 'barefoot', 'soles', 'toes', 'foot_fetish', 'feet_focus'],
    'barefoot': ['feet', 'foot', 'soles', 'toes'],
    'soles': ['feet', 'foot', 'barefoot', 'toes'],
    'toes': ['feet', 'foot', 'barefoot', 'soles'],
    
    // Body part relationships
    'legs': ['thighs', 'feet', 'foot', 'barefoot'],
    'thighs': ['legs', 'thick_thighs'],
    
    // Clothing relationships
    'stockings': ['thighhighs', 'pantyhose', 'socks'],
    'thighhighs': ['stockings', 'pantyhose', 'over-kneehighs'],
    'pantyhose': ['stockings', 'thighhighs'],
    'socks': ['stockings', 'thighhighs'],
    
    // Hair relationships
    'blonde_hair': ['blonde', 'yellow_hair'],
    'brown_hair': ['brunette', 'brown'],
    'black_hair': ['black', 'dark_hair'],
    'red_hair': ['redhead', 'ginger'],
    
    // Eye relationships
    'blue_eyes': ['blue', 'azure_eyes'],
    'green_eyes': ['green', 'emerald_eyes'],
    'brown_eyes': ['brown', 'hazel_eyes'],
    
    // Add more relationships as needed
  }
  
  return relatedTagsMap[searchTag] || []
}

interface BooruBrowserProps {
  sites?: BooruSite[]
  tags?: string[]
  negativeTags?: string[]
  rating?: "safe" | "questionable" | "explicit" | "all" | "unfiltered"
  /** Which ratings to show (see lib/rating-filter.ts). Leave out to show every rating. */
  activeRatings?: FilterRating[]
  sortBy?: "score" | "date" | "random" | "rating" | "downloads" | "favorites" | "relevance" | "duration"
  sortDirection?: "asc" | "desc"
  randomSeed?: number
  dateRange?: PostDateRange
  onPostClick?: (post: BooruPost) => void
  onPostsChange?: (posts: BooruPost[]) => void
  onTagClick?: (tag: string) => void
  allowUnfilteredContent?: boolean
  contentTypeVisibility?: BooruContentTypeVisibility
  /** Lets the empty screen switch back on the content types that hid everything (Filters, Content Filters). */
  onContentTypeVisibilityChange?: (visibility: BooruContentTypeVisibility) => void
  /** "GIFs only": keep just the animated gifs (Filters, Content Filters). */
  gifsOnly?: boolean
  onGifsOnlyChange?: (value: boolean) => void
  followingOnly?: boolean
  followedUsernames?: string[]
}

const MOCK_ALBUM_POST: BooruPost = {
  id: "mock-album",
  site: "danbooru",
  previewUrl: "https://picsum.photos/seed/album-cover/400/600",
  fileUrl: "https://picsum.photos/seed/album-page1/1200/1800",
  tags: ["album", "manga", "doujinshi", "multi-page", "test"],
  score: 1337,
  rating: "safe",
  width: 1200,
  height: 1800,
  type: "album",
  albumImages: [
    "https://picsum.photos/seed/album-page1/1200/1800",
    "https://picsum.photos/seed/album-page2/1200/1800",
    "https://picsum.photos/seed/album-page3/1200/1800",
    "https://picsum.photos/seed/album-page4/1200/1800",
    "https://picsum.photos/seed/album-page5/1200/1800",
    "https://picsum.photos/seed/album-page6/1200/1800",
    "https://picsum.photos/seed/album-page7/1200/1800",
    "https://picsum.photos/seed/album-page8/1200/1800",
    "https://picsum.photos/seed/album-page9/1200/1800",
    "https://picsum.photos/seed/album-page10/1200/1800",
    "https://picsum.photos/seed/album-page11/1200/1800",
    "https://picsum.photos/seed/album-page12/1200/1800",
  ],
}

const MOCK_VIDEO_POST: BooruPost = {
  id: "mock-video",
  site: "paheal",
  previewUrl: "https://picsum.photos/seed/mock-video/400/300",
  fileUrl: "https://example.com/mock-video.mp4",
  tags: ["video", "clip", "test"],
  score: 420,
  rating: "explicit",
  width: 1280,
  height: 720,
  type: "video",
}

const MOCK_GIF_POST: BooruPost = {
  id: "mock-gif",
  site: "paheal",
  previewUrl: "https://picsum.photos/seed/mock-gif/400/300",
  fileUrl: "https://example.com/mock-image.gif",
  tags: ["gif", "loop", "test"],
  score: 69,
  rating: "safe",
  width: 640,
  height: 480,
  type: "image",
}

export function getPostUser(post: BooruPost): string | undefined {
  const raw: any = post.rawPost
  if (!raw || typeof raw !== "object") return undefined

  const candidate =
    raw.uploader ??
    raw.user ??
    raw.owner ??
    raw.author ??
    raw.username ??
    raw.userName ??
    raw.creator ??
    raw.creator_name ??
    raw.uploader_name

  return typeof candidate === "string" ? candidate : undefined
}

/** Nearest ancestor that actually scrolls vertically, or null (= the window). */
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node
    node = node.parentElement
  }
  return null
}

/** How far below the viewport the next page should already be loading: about
 * a screen and a half of scrolling, so a page fetch that takes a few seconds
 * finishes before the user reaches the end of what's loaded. */
function getLoadAheadPx(): number {
  if (typeof window === 'undefined') return 1000
  return Math.max(1000, Math.round(window.innerHeight * 1.5))
}

function fnv1a32(value: string): number {
  let hash = 2166136261
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

/** Tells the visitor a search is still running. Appears only after a short wait so fast loads
 * (most of them, thanks to the shared cache) never flash it. */
function SlowSearchNotice({ message }: { message: string }) {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const id = setTimeout(() => setShow(true), 2500)
    return () => clearTimeout(id)
  }, [])
  if (!show) return null
  return (
    <div role="status" className="flex items-center justify-center gap-2 px-4 pt-4 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}

// Posts hidden by the Content Filters before the grid stops loading more pages (about three pages).
const CONTENT_HIDDEN_STOP_AT = 48
const GIFS_ONLY_FILL_CAP = 300

export function BooruBrowser({
  sites = [],
  tags = [],
  negativeTags = [],
  rating = "all",
  activeRatings: activeRatingsProp,
  sortBy: sortByProp = "score",
  sortDirection: sortDirectionProp = "desc",
  randomSeed = 0,
  dateRange,
  onPostClick,
  onPostsChange,
  onTagClick,
  allowUnfilteredContent = false,
  contentTypeVisibility = { album: true, image: true, video: true },
  onContentTypeVisibilityChange,
  gifsOnly = false,
  onGifsOnlyChange,
  followingOnly = false,
  followedUsernames = [],
}: BooruBrowserProps) {
  const t = useTranslations("booruBrowser")
  const [page, setPage] = useState(0)
  const [allPages, setAllPages] = useState<BooruPost[]>([])
  // Cumulative real-post counts marking where each infinite-scroll fetch
  // ended — e.g. [36, 72] after two batches of 36. Passed to BooruGrid so
  // masonry can render each batch in its own frozen CSS-columns container
  // instead of one continuously-growing one that reflows already-visible
  // posts on every append — see infiniteScrollBatchBoundaries on
  // BooruGridProps for the full story (verified live: images moved by
  // inconsistent pixel amounts on append, proving real reflow, not scroll).
  // Meaningless in pagination mode (each page swaps allPages wholesale), so
  // reset there too, same points where allPages itself resets.
  const [batchBoundaries, setBatchBoundaries] = useState<number[]>([])
  const [touchStartY, setTouchStartY] = useState(0)
  const [isScrollAtBottom, setIsScrollAtBottom] = useState(false)
  const [isLoadingLocked, setIsLoadingLocked] = useState(false)
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const [authDialogSource, setAuthDialogSource] = useState<string | null>(null)
  const [pullDistance, setPullDistance] = useState(0) // Track pull-to-refresh distance
  const [isRefreshing, setIsRefreshing] = useState(false)
  // Pagination pill fade — visible at rest, dims while actively scrolling so
  // it doesn't compete with grid content, reappears a beat after motion stops.
  const [isPaginationPillIdle, setIsPaginationPillIdle] = useState(true)
  const pillIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const observerTarget = useRef<HTMLDivElement | null>(null)
  // The sentinel is tracked in state (as well as the ref the load-zone check
  // reads) so the observer effect re-runs when it actually mounts. The observer
  // used to be built once, up front, before the sentinel existed — fine for
  // attaching later, but it meant the scroll container couldn't be looked up
  // from the sentinel when the observer was created, so it had no `root`.
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null)

  const setObserverTarget = useCallback((node: HTMLDivElement | null) => {
    observerTarget.current = node
    setSentinelEl(node)
  }, [])
  const containerRef = useRef<HTMLDivElement>(null)
  const lastScrollTop = useRef(0)
  // Mirrors of the values the IntersectionObserver's callback needs to read
  // fresh — kept out of that effect's own dependency array on purpose (see
  // the effect below for why: this fixes the "scroll to the bottom twice"
  // infinite-scroll bug).
  const loadingRef = useRef(false)
  const hasMoreRef = useRef(true)
  const isLoadingLockedRef = useRef(false)
  const pageRef = useRef(0)
  // A source can return a page that's 100% duplicates of what we already have
  // — Civitai's /images ignores `page` on cursor-paginated sorts and just
  // replays page 1. Dedup then drops every post, allPages never grows, the
  // sentinel stays parked in the load zone, and the "still in load zone"
  // recheck below re-fires immediately... forever, hammering the upstream API.
  // hasMore can't catch this: the response *was* non-empty, it was just stale.
  // So treat "fetched a non-empty page that added nothing new" as end-of-feed.
  const [feedExhausted, setFeedExhausted] = useState(false)
  const feedExhaustedRef = useRef(false)
  const setActiveRatings = useSettingsStore((state) => state.setActiveRatings)
  const { infiniteScroll, infinitePagesMode, pullToLoadEnabled, pullDistanceThreshold, postsPerPage, filterAIImages, loadingMode: loadingModeSetting } = useSettingsStore()
  const scriptsOnly = useScriptsOnlyStore((s) => s.scriptsOnly)

  // Auto-enable infinite pages mode for XlecX since regular infinite scroll doesn't work well with it
  // UPDATE: XlecX pagination URL is now fixed, so use regular infinite scroll instead
  const shouldUseInfinitePages = infinitePagesMode
  const { isPostBlacklisted, enableBlacklist } = useBlacklistStore()
  const { isPostFiltered, enableFilters } = useTagFiltersStore()
  const { getSource } = useSourceStore()
  // "Auto" resolves per active source: most boorus stream a real infinite
  // feed, but a source flagged preferredLoadingMode: "pagination" (e.g.
  // XlecX, which only has numbered pages) needs pagination even under Auto.
  // Mixed-source sessions fall back to infinite-scroll — there's no clean
  // way to share one Prev/Next cursor across sources with different paging
  // models, so Auto only turns pagination on when every active source wants it.
  const loadingMode = useMemo(() => {
    if (loadingModeSetting !== 'auto') return loadingModeSetting
    const wantsPagination = sites.length > 0 && sites.every((s) => getSource(s)?.preferredLoadingMode === 'pagination')
    return wantsPagination ? 'pagination' : 'infinite-scroll'
  }, [loadingModeSetting, sites, getSource])
  // Subscribed separately (not via the stable getSource accessor) so this component
  // actually re-renders when a board/category selection (e.g. erome's version tabs) changes.
  const allSources = useSourceStore((state) => state.sources)
  const { currentView, currentBoard, currentAlbum, navigateToBoard, navigateToAlbum, navigateBack, saveScrollPosition, getScrollPosition } = useNavigationStore()
  const prevPropsRef = useRef({ sites, tags, negativeTags, rating, sortBy: sortByProp, sortDirection: sortDirectionProp, boardsKey: "" })
  // Order of the posts last shown, so a newly appended page doesn't re-rank into the visible grid (see filteredPosts).
  const stableOrderRef = useRef<{ sig: string; keys: string[] } | null>(null)
  const isInitialMount = useRef(true)
  const lastScrollPosition = useRef(0)
  const hasShownAuthPrompt = useRef(false)
  
  // Check if we're browsing 4chan only
  const is4chanOnly = sites.length === 1 && sites[0] === "4chan"

  // AudioChan filter state — only active when audiochan is the sole/included source
  const isAudioChanOnly = sites.length > 0 && sites.every((s) => s === "audiochan")
  const hasAudioChan = sites.includes("audiochan")
  const [audioChanFilters, setAudioChanFilters] = useState<AudioChanFilters>(DEFAULT_AUDIOCHAN_FILTERS)

  const hasCivitai = sites.includes("civitai")
  const hasRule34 = sites.includes("rule34")
  // Rule34 score tier and best-of window (set from its filter strip). They ride along as marked search terms that only
  // Rule34 acts on; see lib/rule34-filter-terms.ts.
  const [rule34ScoreMin, setRule34ScoreMin] = useState<number | null>(null)
  const [rule34Days, setRule34Days] = useState<number | null>(null)
  const rule34FilterKey = hasRule34 ? rule34FilterTokens(rule34ScoreMin, rule34Days).join(" ") : ""
  // A best-of window means "the top posts of that period", so while one is chosen the list is ordered by score, high to
  // low. The user's own sort choice is untouched and applies again as soon as the window is cleared.
  // e621, e926 and e6AI have their own order menu: Hot, or the best of a day/week/month/year (lib/e621-filter-terms.ts). It travels as its own
  // parameter that only those sources turn into search terms. A best-of window is score order like Rule34's; Hot keeps the site's own ranking.
  const hasE621 = sites.some((site) => E621_FAMILY.includes(site))
  const [e621Order, setE621Order] = useState<E621Order | null>(null)
  const e621Active = hasE621 && e621Order !== null
  const e621HotActive = e621Active && e621Order === "hot"
  // Yande.re has the same kind of menu (best of a day/week/month/year, lib/yandere-filter-terms.ts), also score order inside a window.
  const hasYandere = sites.includes("yandere")
  const [yandereOrder, setYandereOrder] = useState<YandereOrder | null>(null)
  const yandereActive = hasYandere && yandereOrder !== null
  // EpornerVR strip (components/eporner-filter-tabs.tsx): its order and orientation, sent as sourceOptions (read by that source only).
  const hasEporner = sites.includes("eporner-vr")
  const [epornerOrder, setEpornerOrder] = useState<EpornerOrder>("latest")
  const [epornerOrientation, setEpornerOrientation] = useState<EpornerOrientation>("straight")
  const [epornerCategory, setEpornerCategory] = useState("all")
  const epornerKey = hasEporner ? `${epornerOrder}|${epornerOrientation}|${epornerCategory}` : ""
  // XVideos strip: order and orientation through the generic dropdown strip.
  const hasXvideos = sites.includes("xvideos")
  const [xvideosOrder, setXvideosOrder] = useState("relevance")
  const [xvideosOrientation, setXvideosOrientation] = useState("straight")
  const [xvideosCategory, setXvideosCategory] = useState("all")
  const xvideosKey = hasXvideos ? `${xvideosOrder}|${xvideosOrientation}|${xvideosCategory}` : ""
  // ARFreaks category and VR Porn Base studio (one dropdown each).
  const hasArfreaks = sites.includes("arfreaks")
  const [arfreaksCategory, setArfreaksCategory] = useState("all")
  const hasVrpornbase = sites.includes("vrpornbase")
  const [vrpornbaseStudio, setVrpornbaseStudio] = useState("all")
  const hasSankakuStrip = sites.includes("sankaku") || sites.includes("idolsankaku")
  const [sankakuType, setSankakuType] = useState("all")
  const [sankakuOrder, setSankakuOrder] = useState("newest")
  const hasVrhump = sites.includes("vrhump")
  const [vrhumpCategory, setVrhumpCategory] = useState("all")
  const hasSexvr = sites.includes("sexvr")
  const [sexvrChannel, setSexvrChannel] = useState("all")
  const [sexvrOrder, setSexvrOrder] = useState("newest")
  const hasRealbooru = sites.includes("realbooru")
  const [realbooruType, setRealbooruType] = useState("all")
  const hasRedgifs = sites.includes("redgifs")
  const [redgifsType, setRedgifsType] = useState("all")
  const [redgifsOrder, setRedgifsOrder] = useState("latest")
  const baseSourceOptions = useMemo(() => {
    const out: Record<string, Record<string, string>> = {}
    if (hasEporner && epornerKey !== "latest|straight|all") out["eporner-vr"] = { order: epornerOrder, orientation: epornerOrientation, ...(epornerCategory !== "all" ? { category: epornerCategory } : {}) }
    if (hasXvideos && xvideosKey !== "relevance|straight|all") out["xvideos"] = { order: xvideosOrder, orientation: xvideosOrientation, ...(xvideosCategory !== "all" ? { category: xvideosCategory } : {}) }
    if (hasSankakuStrip && (sankakuType !== "all" || sankakuOrder !== "newest")) {
      for (const id of ["sankaku", "idolsankaku"]) if (sites.includes(id)) out[id] = { type: sankakuType, order: sankakuOrder }
    }
    if (hasVrhump && vrhumpCategory !== "all") out["vrhump"] = { category: vrhumpCategory }
    if (hasSexvr && (sexvrChannel !== "all" || sexvrOrder !== "newest")) out["sexvr"] = { ...(sexvrChannel !== "all" ? { channel: sexvrChannel } : {}), order: sexvrOrder }
    if (hasRealbooru && realbooruType !== "all") out["realbooru"] = { type: realbooruType }
    if (hasRedgifs && (redgifsType !== "all" || redgifsOrder !== "latest")) out["redgifs"] = { type: redgifsType, order: redgifsOrder }
    if (hasArfreaks && arfreaksCategory !== "all") out["arfreaks"] = { category: arfreaksCategory }
    if (hasVrpornbase && vrpornbaseStudio !== "all") out["vrpornbase"] = { studio: vrpornbaseStudio }
    return Object.keys(out).length > 0 ? out : undefined
  }, [hasSankakuStrip, sankakuType, sankakuOrder, hasVrhump, vrhumpCategory, hasSexvr, sexvrChannel, sexvrOrder, hasRealbooru, realbooruType, hasRedgifs, redgifsType, redgifsOrder, hasArfreaks, arfreaksCategory, hasVrpornbase, vrpornbaseStudio, hasEporner, epornerKey, epornerOrder, epornerOrientation, epornerCategory, hasXvideos, xvideosKey, xvideosOrder, xvideosOrientation, xvideosCategory])
  // Best-of strip for Gelbooru, Safebooru and Xbooru (lib/best-of-window.ts): the same dropdown as Yande.re's, sent as sourceOptions.
  const bestOfSites = sites.filter((site) => BEST_OF_SITES.includes(site))
  const hasBestOf = bestOfSites.length > 0
  const [bestOf, setBestOf] = useState<YandereOrder | null>(null)
  const bestOfOptionsActive = hasBestOf && bestOf !== null
  const bestOfSitesKey = bestOfSites.join(",")
  // The ratings the viewer has on travel with the strip: Gelbooru's best posts are nearly all explicit, so it has to be asked for only these.
  const bestOfRatings = activeRatingsProp ? [...activeRatingsProp].sort().join("_") : ""
  const sourceOptions = useMemo(() => {
    if (!bestOfOptionsActive) return baseSourceOptions
    const out: Record<string, Record<string, string>> = { ...(baseSourceOptions ?? {}) }
    for (const site of bestOfSitesKey.split(",")) out[site] = { ...(out[site] ?? {}), bestOf: bestOf as string, ...(bestOfRatings ? { ratings: bestOfRatings } : {}) }
    return out
  }, [baseSourceOptions, bestOfOptionsActive, bestOf, bestOfSitesKey, bestOfRatings])
  const sourceOptionsKey = (bestOfOptionsActive ? bestOfSitesKey + ":" + bestOf + ":" + bestOfRatings : "") + "~" + epornerKey + "~" + xvideosKey + "~" + (hasArfreaks ? arfreaksCategory : "") + "~" + (hasVrpornbase ? vrpornbaseStudio : "") + "~" + (hasRedgifs ? redgifsType + redgifsOrder : "") + "~" + (hasRealbooru ? realbooruType : "") + "~" + (hasSexvr ? sexvrChannel + sexvrOrder : "") + "~" + (hasVrhump ? vrhumpCategory : "") + "~" + (hasSankakuStrip ? sankakuType + sankakuOrder : "")
  const bestOfActive = (hasRule34 && rule34Days !== null) || (e621Active && !e621HotActive) || yandereActive || bestOfOptionsActive
  const sortBy = bestOfActive ? "score" : sortByProp
  const sortDirection = bestOfActive ? "desc" : sortDirectionProp
  const [civitaiMediaType, setCivitaiMediaType] = useState<CivitaiMediaType>("all")
  // HentaiEra's top-level section. Empty string = all sections; the source reads it
  // via the `category:<slug>` tag convention its adapter already understands.
  const [hentaieraCategory, setHentaieraCategory] = useState<HentaieraCategory>("")

  // Hitomi's language and type. Empty = all. Sent as one Hitomi-only string of tags ("language:english type:manga") that the source
  // adds to the search; kept out of `tags`, which is shared across sources.
  const [hitomiLanguage, setHitomiLanguage] = useState<HitomiLanguage>("")
  const [hitomiType, setHitomiType] = useState<HitomiType>("")
  const hitomiFilter = hitomiFilterTags(hitomiLanguage, hitomiType)

  // Goontai's genre (the site's own name for it, empty = all); sent as its own parameter, kept out of `tags`.
  const [goontaiGenre, setGoontaiGenre] = useState("")

  const hasLewdvault = sites.includes("lewdvault")
  // LewdVault's sort order. Empty string = Latest (default), "popular" = Hot —
  // same category: tag convention as hentaieraCategory above.
  const [lewdvaultSort, setLewdvaultSort] = useState<LewdvaultSort>("")

  const hasDoujins = sites.includes("doujins")
  // Doujins.com's top-level section. Empty string = Recent (the site's front
  // page); same category: tag convention as hentaieraCategory above.
  const [doujinsCategory, setDoujinsCategory] = useState<DoujinsCategory>("")

  // Restore scroll position when returning to main view
  useEffect(() => {
    if (currentView === 'main' && containerRef.current) {
      const savedPosition = getScrollPosition('main')
      if (savedPosition > 0) {
        console.log('[BooruBrowser] 📜 Restoring main view scroll position:', savedPosition)
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          // containerRef is a non-scrolling wrapper; the real scroller is an
          // ancestor, so setting scrollTop on the wrapper restored nothing.
          const scroller = getScrollParent(containerRef.current)
          if (scroller) scroller.scrollTop = savedPosition
        })
      }
    }
  }, [currentView, getScrollPosition])

  // Save scroll position periodically while scrolling in main view
  useEffect(() => {
    if (currentView !== 'main' || !containerRef.current) return

    // The real scroller, not the wrapper (which never scrolls, so this used to
    // save a permanent 0 and never fire at all).
    const container = getScrollParent(containerRef.current)
    if (!container) return

    const handleScroll = () => {
      const scrollTop = container.scrollTop
      // Debounce saves - only save if scroll position changed significantly (>50px)
      if (Math.abs(scrollTop - lastScrollPosition.current) > 50) {
        lastScrollPosition.current = scrollTop
        saveScrollPosition('main', scrollTop)
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      // Save final scroll position on cleanup
      if (container) {
        saveScrollPosition('main', container.scrollTop)
      }
    }
  }, [currentView, saveScrollPosition])

  // Only scroll to top on search parameter changes, not on mount
  useEffect(() => {
    if (containerRef.current && !isInitialMount.current) {
      getScrollParent(containerRef.current)?.scrollTo({ top: 0 })
    }
  }, [sites, tags, negativeTags, rating])

  // Captures the live board/category selection (e.g. erome's version filter) for
  // each active site, so changing it busts the SWR cache key and triggers a refetch.
  const boardsKey = useMemo(
    () =>
      sites
        .map((site) => `${site}:${allSources.find((s) => s.id === site)?.boards?.selected.join(",") || ""}`)
        .join("|"),
    [sites, allSources],
  )

  const searchParams = useMemo(
    () => {
      const params = {
        tags: rule34FilterKey ? [...tags, ...rule34FilterKey.split(" ")] : tags,
        negativeTags,
        rating,
        limit: postsPerPage,
        page,
        boardsKey,
        audioChanSort: audioChanFilters.sort,
        civitaiMediaType,
        e621Order: e621Active ? e621Order : undefined,
        yandereOrder: yandereActive ? yandereOrder : undefined,
        sourceOptions,
        hentaieraCategory,
        hitomiFilter,
        goontaiGenre,
        lewdvaultSort,
        doujinsCategory,
        // Only Score and Date are sorted by the source itself; every other choice (random, rating, favorites...) is
        // applied in the browser, so those must not change the request (and refetch) when picked.
        sortBy: sortBy === "score" || sortBy === "date" ? sortBy : undefined,
        sortDirection: sortBy === "score" || sortBy === "date" ? sortDirection : undefined,
      }
      console.log("[BooruBrowser] searchParams created:", {
        ...params,
        postsPerPage,
        currentPage: page
      })
      return params
    },
    [tags, negativeTags, rating, postsPerPage, page, boardsKey, audioChanFilters.sort, civitaiMediaType, rule34FilterKey, e621Active ? e621Order : null, yandereActive ? yandereOrder : null, sourceOptionsKey, hentaieraCategory, hitomiFilter, goontaiGenre, lewdvaultSort, doujinsCategory, sortBy, sortDirection],
  )

  // Scripts-Only mode swaps the feed's entire data source (community_scripts
  // instead of the live per-source APIs) — see docs/SCRIPTS_ONLY_GRID_TASK_PLAN.md.
  // Both hooks are always called to satisfy the rules of hooks; the inactive
  // one is handed a null-key input so it never fetches. Everything downstream
  // of this line is untouched, because both return the same shape and the
  // scripts are adapted into real BooruPost objects.
  const booruSearchResult = useBooruSearch(scriptsOnly || sites.length === 0 ? [] : sites, searchParams)
  const scriptsOnlyResult = useScriptsOnlyPosts(scriptsOnly, page, postsPerPage)
  const { posts, loading, error, hasMore } = scriptsOnly ? scriptsOnlyResult : booruSearchResult

  // Kept in sync every render (cheap — refs don't trigger re-renders) so the
  // IntersectionObserver's callback below always reads fresh values without
  // needing the observer itself torn down and recreated whenever any of
  // these change — see the observer-setup effect for why that matters.
  useEffect(() => {
    loadingRef.current = loading
    hasMoreRef.current = hasMore
    isLoadingLockedRef.current = isLoadingLocked
    pageRef.current = page
    feedExhaustedRef.current = feedExhausted
  }, [loading, hasMore, isLoadingLocked, page, feedExhausted])
  
  console.log('[BooruBrowser] useBooruSearch result:', {
    postsCount: posts.length,
    loading,
    hasMore,
    error: error?.message,
    currentPage: page,
    sites: sites.join(',')
  })

  useEffect(() => {
    console.log('[BooruBrowser] Posts update:', {
      newPostsCount: posts.length,
      currentAllPagesCount: allPages.length,
      sites: sites.join(',')
    })
    
    if (posts.length > 0) {
      // Pagination mode shows exactly one page's worth of content at a time —
      // each page swaps the grid rather than growing it forever, which is
      // the actual distinction from infinite scroll (previously both modes
      // fed the same ever-appending allPages array, so "pagination" secretly
      // behaved like manually-triggered infinite scroll under the hood).
      if (loadingMode === 'pagination') {
        setAllPages(posts)
        setBatchBoundaries([])
        // containerRef is a non-scrolling wrapper (setting its scrollTop did
        // nothing — a page swap left you wherever you were, measured 146px
        // down on the new page). The real scroller is an ancestor.
        getScrollParent(containerRef.current)?.scrollTo({ top: 0 })
        return
      }

      setAllPages((prev) => {
        // Use site-id combination for proper deduplication
        const existingKeys = new Set(prev.map((p) => `${p.site}-${p.id}`))
        const newPosts = posts.filter((p) => !existingKeys.has(`${p.site}-${p.id}`))

        if (newPosts.length === 0) {
          // Only conclude the source replayed a page once the fetch has
          // actually settled. useBooruSearch runs SWR with
          // keepPreviousData: true, so mid-transition `posts` still holds the
          // PREVIOUS page's array while `loading` is true — which dedups to
          // zero new every single time and would trip the exhausted flag on
          // every page turn, killing infinite scroll after one page.
          if (!loading) {
            console.log('[BooruBrowser] No new posts to add (all duplicates) — marking feed exhausted')
            setFeedExhausted(true)
            feedExhaustedRef.current = true
          } else {
            console.log('[BooruBrowser] No new posts yet (stale keepPreviousData during fetch) — not marking exhausted')
          }
          return prev
        }

        console.log(`[BooruBrowser] Adding ${newPosts.length} new posts (filtered ${posts.length - newPosts.length} duplicates)`)
        console.log('[BooruBrowser] First new post:', {
          id: newPosts[0].id,
          site: newPosts[0].site,
          title: newPosts[0].title,
          hasFileUrl: !!newPosts[0].fileUrl,
          hasPreviewUrl: !!newPosts[0].previewUrl
        })

        // Only add mock posts on first load and if we're browsing Danbooru/Paheal
        // Never in Scripts-Only — the mocks are source demo content with no
        // attached scripts, so they'd contradict the whole point of the mode.
        // Off unless NEXT_PUBLIC_BOORU_MOCKS=1: they used to be added for everyone, and showed up on live as broken cells.
        const shouldAddMocks = process.env.NEXT_PUBLIC_BOORU_MOCKS === "1" && !scriptsOnly && prev.length === 0 && sites.some(s => s === 'danbooru' || s === 'paheal')
        const mockPosts: BooruPost[] = shouldAddMocks ? [MOCK_ALBUM_POST, MOCK_VIDEO_POST, MOCK_GIF_POST] : []
        const withMocks = mockPosts.length > 0 ? [...mockPosts, ...prev, ...newPosts] : [...prev, ...newPosts]

        console.log('[BooruBrowser] Total posts after update:', withMocks.length)

        // Record this batch's boundary — see the batchBoundaries state doc
        // comment above. withMocks.length is the running total AFTER this
        // append, which is exactly the cumulative-count marker BooruGrid
        // expects.
        //
        // Idempotency guard: this setBatchBoundaries call lives inside the
        // setAllPages functional updater above, and React (Strict Mode, dev
        // only) deliberately double-invokes updater functions to catch
        // exactly this kind of nested-side-effect impurity. Both
        // invocations receive the identical `prev`/`posts` snapshot, so
        // `withMocks.length` computes to the same value both times — without
        // this guard that pushed a duplicate boundary a few posts after the
        // real one (e.g. [36, 37, 72, 73] instead of [36, 72]), leaving a
        // near-empty 1-item "batch" between every real one. BooruGrid gives
        // each batch its own CSS-columns container, and a container holding
        // 1 item across `columnCount: 5` renders that item in column 1 with
        // columns 2-5 entirely blank for that row's height — reported live
        // as new, worse empty space than before this batching feature
        // existed at all.
        setBatchBoundaries((prevBoundaries) =>
          prevBoundaries[prevBoundaries.length - 1] === withMocks.length
            ? prevBoundaries
            : [...prevBoundaries, withMocks.length]
        )

        // Preserve scroll position when adding new posts (for infinite scroll)
        // New posts are added at the BOTTOM, so we just need to maintain current scroll position
        // No adjustment needed - browser naturally keeps scroll position when content is added below
        if (containerRef.current && newPosts.length > 0 && prev.length > 0) {
          const savedScrollTop = containerRef.current.scrollTop

          console.log('[BooruBrowser] Preserving scroll position:', {
            savedScrollTop,
            scrollHeight: containerRef.current.scrollHeight,
            clientHeight: containerRef.current.clientHeight
          })

          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            if (containerRef.current && savedScrollTop > 0) {
              // Restore exact scroll position
              containerRef.current.scrollTop = savedScrollTop
              console.log('[BooruBrowser] Restored scroll position to:', savedScrollTop)
            }
          })
        }

        return withMocks
      })
    } else if (loadingMode === 'pagination' && !loading && !error) {
      // A page with zero results (e.g. paged past the end) still needs to
      // clear out the previous page's posts instead of leaving them stuck.
      //
      // The `!error` guard matters: a transient upstream failure also
      // surfaces as zero posts, and without it a single flaky response
      // wipes the grid to completely blank rather than leaving the page
      // the user was already looking at. Seen live paging back from page 2
      // to page 1 — the pill correctly returned to "Page 1" but the grid
      // rendered nothing, and the identical navigation succeeded on a
      // retry. An errored fetch should leave the current page alone; only
      // a genuine, successful empty result should clear it.
      setAllPages([])
      setBatchBoundaries([])
    }
  }, [posts, sites, loadingMode, loading, error, scriptsOnly])

  useEffect(() => {
    const prev = prevPropsRef.current
    const hasChanged =
      JSON.stringify(sites) !== JSON.stringify(prev.sites) ||
      JSON.stringify(tags) !== JSON.stringify(prev.tags) ||
      JSON.stringify(negativeTags) !== JSON.stringify(prev.negativeTags) ||
      rating !== prev.rating ||
      sortBy !== prev.sortBy ||
      sortDirection !== prev.sortDirection ||
      boardsKey !== prev.boardsKey ||
      audioChanFilters.sort !== (prev as any).audioChanSort ||
      civitaiMediaType !== (prev as any).civitaiMediaType ||
      rule34FilterKey !== ((prev as any).rule34FilterKey ?? "") ||
      (e621Active ? e621Order : null) !== ((prev as any).e621Order ?? null) ||
      (yandereActive ? yandereOrder : null) !== ((prev as any).yandereOrder ?? null) ||
      sourceOptionsKey !== ((prev as any).sourceOptionsKey ?? "") ||
      hentaieraCategory !== (prev as any).hentaieraCategory ||
      hitomiFilter !== ((prev as any).hitomiFilter ?? "") ||
      goontaiGenre !== ((prev as any).goontaiGenre ?? "") ||
      lewdvaultSort !== (prev as any).lewdvaultSort ||
      doujinsCategory !== (prev as any).doujinsCategory
      // dateRange is deliberately not here: it is applied to the loaded posts in filteredPosts, so changing it must not
      // wipe and reload the list (that also briefly looked like "no results" and could pop the auth prompt).

    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      prevPropsRef.current = { sites, tags, negativeTags, rating, sortBy, sortDirection, boardsKey, audioChanSort: audioChanFilters.sort, civitaiMediaType, rule34FilterKey, e621Order: e621Active ? e621Order : null, yandereOrder: yandereActive ? yandereOrder : null, sourceOptionsKey, hentaieraCategory, hitomiFilter, goontaiGenre, lewdvaultSort, doujinsCategory } as any
      return
    }

    // Only reset if props actually changed
    if (hasChanged) {
      console.log("[v0] Search params changed - resetting posts")
      setAllPages([])
      setBatchBoundaries([])
      setPage(0)
      setFeedExhausted(false)
      feedExhaustedRef.current = false
      hasShownAuthPrompt.current = false // Reset auth prompt flag
      prevPropsRef.current = { sites, tags, negativeTags, rating, sortBy, sortDirection, boardsKey, audioChanSort: audioChanFilters.sort, civitaiMediaType, rule34FilterKey, e621Order: e621Active ? e621Order : null, yandereOrder: yandereActive ? yandereOrder : null, sourceOptionsKey, hentaieraCategory, hitomiFilter, goontaiGenre, lewdvaultSort, doujinsCategory } as any
    }
  }, [sites, tags, negativeTags, rating, sortBy, sortDirection, boardsKey, audioChanFilters.sort, civitaiMediaType, rule34FilterKey, e621Active ? e621Order : null, yandereActive ? yandereOrder : null, sourceOptionsKey, hentaieraCategory, hitomiFilter, goontaiGenre, lewdvaultSort, doujinsCategory])

  // Switching into or out of Scripts-Only swaps the feed's data source, so the
  // accumulated pages have to go — otherwise the two sources' posts interleave
  // in one list. Kept as its own effect rather than folded into the prop-diff
  // above because it isn't a prop, and that block's prevPropsRef bookkeeping
  // would have to grow a special case for it.
  const prevScriptsOnlyRef = useRef(scriptsOnly)
  useEffect(() => {
    if (prevScriptsOnlyRef.current === scriptsOnly) return
    prevScriptsOnlyRef.current = scriptsOnly
    setAllPages([])
    setBatchBoundaries([])
    setPage(0)
    setFeedExhausted(false)
    feedExhaustedRef.current = false
  }, [scriptsOnly])

  // Show auth dialog when no posts load after initial search
  useEffect(() => {
    // Not in Scripts-Only: an empty result there means "no scripted content",
    // which has nothing to do with source credentials.
    if (!scriptsOnly && !loading && !error && allPages.length === 0 && sites.length > 0 && !hasShownAuthPrompt.current && page === 0) {
      // Wait a bit to ensure it's not just loading
      const timer = setTimeout(() => {
        // A search with tags that finds nothing is just "no results" (a typo, or a tag the platform hides), not a sign the
        // source wants a login, so only an empty default feed prompts. Before this, a blocked tag search opened the
        // "Authentication is required" dialog for sources that need no login at all.
        if (allPages.length === 0 && sites.length > 0 && tags.length === 0) {
          hasShownAuthPrompt.current = true
          
          // Check if any of the CURRENTLY SELECTED sites require auth
          const { getCredentials, isPromptDismissed } = useBooruCredentialsStore.getState()
          const sitesRequiringAuth = sites.filter(site => {
            const config = getSource(site)

            // Skip disabled sources
            if (!config || !config.enabled) return false

            // Never prompt for a source with no credential system. Checks the
            // hardcoded id list as well as the config's own requiresAuth flag,
            // because a persisted config saved before that flag existed reads
            // as undefined here and would otherwise fall through and prompt —
            // see SOURCES_WITHOUT_CREDENTIALS in lib/source-store.ts.
            if (sourceHasNoCredentials(config) || sourceHasNoCredentials(site)) return false

            // Respect a persisted "don't ask again" — without this, the popup reappears on
            // every filter/tag change (hasShownAuthPrompt is a per-search ref, not per-source),
            // which is what made it feel like it never stops nagging.
            if (isPromptDismissed(site)) return false

            // Also check if credentials are disabled - if so, don't show auth dialog
            const creds = getCredentials(site)

            // If credentials are disabled (getCredentials returns undefined), don't show dialog
            return creds === undefined
          })
          
          // Only show auth dialog if at least one site requires auth
          if (sitesRequiringAuth.length > 0) {
            setAuthDialogSource(sitesRequiringAuth[0])
            setAuthDialogOpen(true)
          }
        }
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [loading, error, allPages.length, sites, tags, page, getSource, scriptsOnly])

  useEffect(() => {
    const container = containerRef.current
    // Pull-to-load is an infinite-scroll gesture (drag at the bottom to
    // fetch more). It used to fire regardless of loadingMode, so a drag
    // while pagination was on would silently bump `page` out from under
    // the pill's own state — this is almost certainly the "pagination
    // doesn't work" report, since the two navigation methods were fighting
    // over the same counter. Pagination now has its own explicit prev/next
    // controls, so this gesture is infinite-scroll-only.
    if (!container || !pullToLoadEnabled || loadingMode !== 'infinite-scroll') return

    const handleTouchStart = (e: TouchEvent) => {
      // Prevent default to avoid scroll conflicts
      if (isScrollAtBottom) {
        e.preventDefault()
      }
      setTouchStartY(e.touches[0].clientY)
      // Check if scrolled to bottom
      // Measure the real scroller — the wrapper never scrolls, so its numbers
      // made this always read "at the bottom".
      const scroller = getScrollParent(container) ?? container
      const isAtBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 10
      setIsScrollAtBottom(isAtBottom)
    }

    const handleTouchMove = (e: TouchEvent) => {
      // Prevent default scroll when at bottom and pulling
      if (isScrollAtBottom && e.touches[0].clientY < touchStartY) {
        e.preventDefault()
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndY = e.changedTouches[0].clientY
      const pullDistance = touchStartY - touchEndY // Positive when pulling DOWN

      // If at bottom and pulled down at least threshold distance, load more
      if (isScrollAtBottom && pullDistance > pullDistanceThreshold && !loading && !isLoadingLocked) {
        setIsLoadingLocked(true)
        setPage((p) => p + 1)
        // Unlock after a short delay to prevent rapid triggers
        setTimeout(() => setIsLoadingLocked(false), 1000)
      }
      setIsScrollAtBottom(false)
    }

    container.addEventListener("touchstart", handleTouchStart, { passive: false })
    container.addEventListener("touchmove", handleTouchMove, { passive: false })
    container.addEventListener("touchend", handleTouchEnd)

    return () => {
      container.removeEventListener("touchstart", handleTouchStart)
      container.removeEventListener("touchmove", handleTouchMove)
      container.removeEventListener("touchend", handleTouchEnd)
    }
  }, [pullToLoadEnabled, pullDistanceThreshold, loading, isScrollAtBottom, touchStartY, isLoadingLocked, loadingMode])

  // Pagination pill fade: dim while the grid is actively scrolling, come
  // back a moment after it stops. Only wired up in pagination mode.
  useEffect(() => {
    if (loadingMode !== 'pagination') return

    // Scroll events don't bubble, and containerRef is a non-scrolling wrapper
    // INSIDE the real scroller — so a listener on it never fired and the pill
    // never dimmed (measured: opacity stayed 1 while scrolling). Listen in the
    // capture phase on the document instead and keep only scrolls from an
    // element that contains the grid, which works whichever ancestor scrolls
    // and whenever it mounts.
    const handleScroll = (e: Event) => {
      const grid = containerRef.current
      const target = e.target
      if (!grid || !(target instanceof Node) || !target.contains(grid)) return
      setIsPaginationPillIdle(false)
      if (pillIdleTimerRef.current) clearTimeout(pillIdleTimerRef.current)
      pillIdleTimerRef.current = setTimeout(() => setIsPaginationPillIdle(true), 500)
    }

    document.addEventListener('scroll', handleScroll, { capture: true, passive: true })
    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true })
      if (pillIdleTimerRef.current) clearTimeout(pillIdleTimerRef.current)
    }
  }, [loadingMode])

  // Pull-to-refresh disabled to prevent scroll lag and accidental refreshes
  // Users can use the refresh button in the UI instead

  // A stable string (the prop's array identity changes every render) so the memo below only recomputes when the
  // excluded tags really change.
  const excludedTagKey = negativeTags.filter((tag) => tag.trim()).join("\u0001")
  const activeRatingsKey = activeRatingsProp ? [...activeRatingsProp].sort().join(",") : ""
  const activeRatings = useMemo(
    () => (activeRatingsKey ? (activeRatingsKey.split(",") as FilterRating[]) : undefined),
    [activeRatingsKey],
  )

  const filteredPosts = useMemo(() => {
    console.log('[v0] BooruBrowser filtering posts', {
      totalPosts: allPages.length, 
      enableFilters, 
      enableBlacklist, 
      filterAIImages,
      contentTypeVisibility,
      allowUnfilteredContent
    })
    // Posts with no image address (Danbooru's still-pending uploads) would be blank cells.
    let result = allPages.filter(postHasShowableImage)

    const beforeContentType = result.length
    result = result.filter((post) => [...getPostMediaTypes(post)].some((type) => contentTypeVisibility[type]))
    console.log('[v0] After contentType filter:', { before: beforeContentType, after: result.length, removed: beforeContentType - result.length })
    if (gifsOnly) result = result.filter(isGifPost)

    // Excluded search tags ("-solo"). The sources never received these (negativeTags was dropped when the query was
    // built), so apply them here: exact tag match, and posts with no tag data are kept.
    if (excludedTagKey) {
      const excluded = excludedTagKey.split("\u0001")
      result = result.filter((post) => !postHasExcludedTag(post.tags, excluded))
    }

    // Rating filter: only the ratings the viewer switched on stay. Omitted by callers that don't have one (they keep
    // seeing everything, as before). Extreme posts follow the Unfiltered Content switch.
    if (activeRatings) {
      result = result.filter((post) => postMatchesRatings(post, activeRatings, allowUnfilteredContent))
    }

    if (!allowUnfilteredContent) {
      const beforeNsfwu = result.length
      result = result.filter((post) => !post.isNsfwu)
      console.log('[v0] After NSFWU filter:', { before: beforeNsfwu, after: result.length, removed: beforeNsfwu - result.length })
    }

    if (enableFilters) {
      const beforeFilters = result.length
      result = result.filter((post) => !isPostFiltered(post.tags))
      console.log('[v0] After tag filters:', { before: beforeFilters, after: result.length, removed: beforeFilters - result.length })
    }

    if (enableBlacklist) {
      const beforeBlacklist = result.length
      result = result.filter((post) => !isPostBlacklisted(post.tags, getPostUser(post)))
      console.log('[v0] After blacklist:', { before: beforeBlacklist, after: result.length, removed: beforeBlacklist - result.length })
    }

    if (filterAIImages) {
      const beforeAI = result.length
      // lib/ai-content.ts knows the many ways sources mark AI work (tags, tool names, AI-only sources)
      result = result.filter((post) => !isAiPost(post))
      console.log('[v0] After AI filter:', { before: beforeAI, after: result.length, removed: beforeAI - result.length })
    }

    // Date range: keep posts made inside the range. The date comes from whichever field the source provides (see
    // lib/post-date.ts); a post with no usable date is left out while a range is on.
    if (dateRange) {
      result = result.filter((post) => postInDateRange(post, dateRange))
    }

    // Following filter — only show posts from creators the user follows
    if (followingOnly && followedUsernames.length > 0) {
      const lowerNames = new Set(followedUsernames.map((u) => u.toLowerCase()))
      result = result.filter((post) => {
        const user = getPostUser(post)
        return user ? lowerNames.has(user.toLowerCase()) : false
      })
    }

    console.log('[v0] Final filtered posts:', result.length)

    // Sorting
    const sorted = [...result]
    
    // For infinite scroll with date sorting, preserve insertion order from API
    // This ensures older posts stay at the bottom as you scroll
    // Hot is the site's own ranking, not a sort we can redo from score or date, so it keeps the order the site sent.
    const shouldPreserveOrder = e621HotActive || (infiniteScroll && sortBy === "date" && sortDirection === "desc")
    
    if (shouldPreserveOrder) {
      // Keep posts in the order they were added (API order)
      // No sorting needed - posts are already newest first from API
    } else {
      switch (sortBy) {
        case "score":
          sorted.sort((a, b) => (sortDirection === "desc" ? b.score - a.score : a.score - b.score))
          break
        case "date":
          // For infinite scroll, preserve the order posts were added (chronological from API)
          // Posts are already in the correct order from the API (newest first)
          // Only reverse if user wants ascending order (oldest first)
          if (sortDirection === "asc") {
            sorted.reverse()
          }
          // Otherwise keep the insertion order (newest at top, older posts appended as you scroll)
          break
        case "random":
          sorted.sort((a, b) => {
            const aKey = fnv1a32(`${randomSeed}:${a.site ?? ""}:${a.id}`)
            const bKey = fnv1a32(`${randomSeed}:${b.site ?? ""}:${b.id}`)
            return aKey - bKey || String(a.id).localeCompare(String(b.id))
          })
          break
        case "rating":
          sorted.sort((a, b) => {
            const aRating = a.site ? useUserRatingsStore.getState().getRating(a.site, String(a.id)) : 0
            const bRating = b.site ? useUserRatingsStore.getState().getRating(b.site, String(b.id)) : 0
            return sortDirection === "desc" ? bRating - aRating : aRating - bRating
          })
          break
        case "downloads":
          sorted.sort((a, b) => {
            const aDownloads = a.site
              ? useUserRatingsStore.getState().getDownloadCount(a.site, String(a.id))
              : 0
            const bDownloads = b.site
              ? useUserRatingsStore.getState().getDownloadCount(b.site, String(b.id))
              : 0
            return sortDirection === "desc" ? bDownloads - aDownloads : aDownloads - bDownloads
          })
          break
        case "favorites":
          sorted.sort((a, b) => {
            const aFav = useFavoritesStore.getState().isFavorite(`${a.site}-${a.id}`) ? 1 : 0
            const bFav = useFavoritesStore.getState().isFavorite(`${b.site}-${b.id}`) ? 1 : 0
            return sortDirection === "desc" ? bFav - aFav : aFav - bFav
          })
          break
        case "relevance":
          sorted.sort((a, b) => {
            const aRelevance = calculateRelevanceScore(a, tags || [])
            const bRelevance = calculateRelevanceScore(b, tags || [])
            return sortDirection === "desc" ? bRelevance - aRelevance : aRelevance - bRelevance
          })
          break
        case "duration":
          // Only sorts whatever's currently loaded — most sources don't expose
          // duration at all, so those posts (durationSeconds undefined) always
          // sink to the bottom regardless of direction, rather than being
          // treated as zero-length and cluttering the top of an ascending sort.
          sorted.sort((a, b) => {
            const aHas = typeof a.durationSeconds === "number"
            const bHas = typeof b.durationSeconds === "number"
            if (!aHas && !bHas) return 0
            if (!aHas) return 1
            if (!bHas) return -1
            return sortDirection === "desc"
              ? b.durationSeconds! - a.durationSeconds!
              : a.durationSeconds! - b.durationSeconds!
          })
          break
      }
    }

    // Keep what is already on screen where it is. Re-sorting the WHOLE accumulated list on every append
    // (default sort is "score") made each newly loaded page rank into the middle of the grid: posts
    // already visible got displaced or re-ordered when page 2 arrived (measured: a card inserted at the top
    // of the second column, ~0.25 layout shift). Under the same sort/filter inputs, posts already shown keep
    // their order and the newly arrived ones follow, sorted among themselves. Any change to the sort, the
    // filters, the search or the sources starts a fresh order.
    const orderSig = [
      sortBy, sortDirection, randomSeed, dateRange ? JSON.stringify(dateRange) : "", activeRatingsKey, excludedTagKey, e621HotActive,
      filterAIImages, enableFilters, enableBlacklist, allowUnfilteredContent, followingOnly, String(tags || ""), String(sites || ""),
    ].join("~")
    const prevOrder = stableOrderRef.current
    const keyOf = (p: BooruPost) => `${p.site}-${p.id}`
    let ordered = sorted
    if (prevOrder && prevOrder.sig === orderSig && prevOrder.keys.length > 0) {
      const rank = new Map(prevOrder.keys.map((k, i) => [k, i]))
      const existing: BooruPost[] = []
      const fresh: BooruPost[] = []
      for (const p of sorted) (rank.has(keyOf(p)) ? existing : fresh).push(p)
      // Nothing carried over (a brand new result set): just use the sorted order.
      if (existing.length > 0) {
        existing.sort((a, b) => (rank.get(keyOf(a)) as number) - (rank.get(keyOf(b)) as number))
        ordered = [...existing, ...fresh]
      }
    }
    stableOrderRef.current = { sig: orderSig, keys: ordered.map(keyOf) }
    return ordered
  }, [
    allPages,
    allowUnfilteredContent,
    isPostFiltered,
    enableFilters,
    isPostBlacklisted,
    enableBlacklist,
    excludedTagKey,
    activeRatings,
    filterAIImages,
    sortBy,
    sortDirection,
    randomSeed,
    dateRange,
    contentTypeVisibility,
    gifsOnly,
    infiniteScroll,
    activeRatingsKey,
    followingOnly,
    tags,
    sites,
  ])

  // Hoisted out of the observer-setup effect (and out of two JSX
  // conditions that used to recompute this inline) into its own memo — a
  // stable boolean the effect can depend on instead of the raw
  // filteredPosts array, which gets a new reference on nearly every render.
  const isShowingCategories = useMemo(
    () => filteredPosts.length > 0 && filteredPosts.every((post) => post.rawPost?.isCategory === true),
    [filteredPosts],
  )

  // The same for the Content Filters (image / video / album): when a source strip fetches videos but the filter shows images only,
  // nothing ever shows, and without this the grid is just empty.
  const hiddenByContentType = useMemo(
    () => summarizeHiddenByContentType(allPages.filter(postHasShowableImage), contentTypeVisibility),
    [allPages, contentTypeVisibility],
  )
  // Posts the content types allow but "GIFs only" removed.
  const hiddenByGifsOnly = useMemo(
    () => (gifsOnly ? allPages.filter(postHasShowableImage).filter((post) => [...getPostMediaTypes(post)].some((type) => contentTypeVisibility[type]) && !isGifPost(post)).length : 0),
    [allPages, contentTypeVisibility, gifsOnly],
  )

  // When the rating pills leave nothing to show, this says how many posts they hid, so the empty screen can offer them.
  const hiddenByRating = useMemo(
    () => (activeRatings ? summarizeHiddenByRating(allPages.filter(postHasShowableImage), activeRatings, allowUnfilteredContent) : { count: 0, ratings: [] as FilterRating[] }),
    [allPages, activeRatings, allowUnfilteredContent],
  )

  const audioFilteredPosts = useMemo(
    () =>
      filteredPosts.filter((p) => {
        if (p.site !== "audiochan") return true
        const isNsfw = p.rating === "explicit"
        if (audioChanFilters.contentMode === "sfw" && isNsfw) return false
        if (audioChanFilters.contentMode === "nsfw" && !isNsfw) return false
        if (
          audioChanFilters.audience !== "all" &&
          Array.isArray(p.audioAudienceType) &&
          p.audioAudienceType.length > 0 &&
          !p.audioAudienceType.some(
            (a: string) => a.toUpperCase() === audioChanFilters.audience.toUpperCase(),
          )
        )
          return false
        return true
      }),
    [filteredPosts, audioChanFilters],
  )

  useEffect(() => {
    if (onPostsChange && filteredPosts.length > 0) {
      onPostsChange(filteredPosts)
    }
  }, [filteredPosts, onPostsChange])

  // Deliberately NOT depending on loading/hasMore/isLoadingLocked/page here
  // — those all change during a normal load cycle (several times per
  // scroll), and re-running this effect tears down and recreates the
  // observer every time. A freshly-created observer only fires its callback
  // once, reporting whatever the sentinel's intersection state happens to
  // be AT THAT INSTANT — if a teardown/recreate cycle lands at a moment
  // where the sentinel isn't currently intersecting, nothing fires again
  // until a genuine new enter event, which needs the user to scroll away
  // and back. That's the exact "have to scroll to the bottom twice" bug.
  // Fix: create the observer once (per loadingMode/isShowingCategories
  // change, which genuinely should reset it) and read the guard values
  // through the refs above, which the effect right below keeps fresh.
  useEffect(() => {
    console.log('[InfiniteScroll] Setup check:', {
      infiniteScroll,
      loadingMode,
      isShowingCategories,
    })

    if (loadingMode !== 'infinite-scroll') {
      console.log('[InfiniteScroll] Disabled - skipping observer setup')
      return
    }

    // Don't enable infinite scroll for category pages
    if (isShowingCategories) {
      console.log('[InfiniteScroll] Showing categories - skipping observer setup')
      return
    }

    if (!sentinelEl) {
      console.log('[InfiniteScroll] No sentinel mounted yet — waiting for it')
      return
    }

    console.log('[InfiniteScroll] Setting up IntersectionObserver')

    // Measured live 2026-09-30 (Erome grid): the next-page request fired at
    // exactly 0px from the bottom, so the old `rootMargin: '400px'` did
    // nothing. The grid doesn't scroll the window — it scrolls inside an
    // overflow-y-auto ancestor, and an IntersectionObserver with no `root`
    // clips the sentinel at that ancestor's edge before the margin is
    // applied. Pointing `root` at the real scroller makes the lead work.
    const observer = new IntersectionObserver(
      (entries) => {
        const loading = loadingRef.current
        const hasMore = hasMoreRef.current
        const isLoadingLocked = isLoadingLockedRef.current
        const page = pageRef.current
        console.log('[InfiniteScroll] Observer triggered:', {
          isIntersecting: entries[0].isIntersecting,
          loading,
          hasMore,
          isLoadingLocked,
          currentPage: page
        })

        if (entries[0].isIntersecting && !loading && hasMore && !isLoadingLocked && !feedExhaustedRef.current) {
          console.log('[InfiniteScroll] ✅ Loading next page:', page + 1)
          setIsLoadingLocked(true)
          isLoadingLockedRef.current = true
          setPage((p) => {
            console.log('[InfiniteScroll] Page increment:', p, '->', p + 1)
            return p + 1
          })
          // Add delay to prevent rapid-fire requests
          setTimeout(() => {
            console.log('[InfiniteScroll] Unlocking after delay')
            setIsLoadingLocked(false)
            isLoadingLockedRef.current = false
          }, 250)
        } else {
          console.log('[InfiniteScroll] ❌ Not loading because:', {
            notIntersecting: !entries[0].isIntersecting,
            alreadyLoading: loading,
            noMore: !hasMore,
            locked: isLoadingLocked,
            // Must be listed here too — omitting it made this log print
            // "all four reasons false" while still not loading.
            feedExhausted: feedExhaustedRef.current,
          })
        }
      },
      {
        root: getScrollParent(sentinelEl),
        threshold: 0.1,
        rootMargin: `0px 0px ${getLoadAheadPx()}px 0px`,
      },
    )

    observer.observe(sentinelEl)

    return () => {
      console.log('[InfiniteScroll] Cleaning up observer')
      observer.disconnect()
    }
  }, [infiniteScroll, loadingMode, isShowingCategories, sentinelEl])

  // The observer above only fires on a genuine intersection-state change —
  // it won't refire just because a page finished loading while the sentinel
  // was already sitting in the trigger zone (e.g. a short first page on a
  // tall viewport). This explicitly re-checks the sentinel's actual
  // position once loading completes and, if it's still within the load
  // zone, triggers the next page directly — a real correctness check
  // (getBoundingClientRect), not relying on the observer to have happened
  // to recreate itself at the right moment the old code's churn provided
  // as an accidental side effect.
  //
  // filteredPosts.length is also a dep: toggling the All/Image/Video
  // content-type visibility filter doesn't fetch anything, it just hides
  // already-loaded posts client-side. That can collapse a tall page down
  // to a near-empty one (e.g. switching to "Video" on a source where most
  // loaded posts are images) without any scroll event ever firing to wake
  // the IntersectionObserver — leaving the sentinel stranded in the load
  // zone with nothing re-checking it. Re-running this check when the
  // filtered count changes closes that gap.
  useEffect(() => {
    if (loading || loadingMode !== 'infinite-scroll' || !hasMore || isShowingCategories || feedExhausted) return
    // Everything fetched so far is hidden by the rating pills: an empty grid always has the sentinel in view, so without
    // this it would fetch page after page nobody can see. The empty screen offers the hidden posts instead.
    if (filteredPosts.length === 0 && hiddenByRating.count > 0) return
    // The content filter does keep loading on purpose (a page of mostly images can hold no video yet), but when three pages in a row
    // were all hidden it is a clash with the filter, not a thin page: stop and say so on the empty screen.
    if (filteredPosts.length === 0 && hiddenByContentType.count >= CONTENT_HIDDEN_STOP_AT) return
    if (filteredPosts.length === 0 && hiddenByGifsOnly >= CONTENT_HIDDEN_STOP_AT) return
    // GIFs only on a source where gifs are rare would keep filling a short grid page after page (Paheal: ~26 pages for 16 gifs).
    // Past this many hidden posts the grid stops filling itself; scrolling down still loads the next page.
    if (hiddenByGifsOnly >= GIFS_ONLY_FILL_CAP) return
    const el = observerTarget.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const stillInLoadZone = rect.top <= (typeof window !== 'undefined' ? window.innerHeight : 0) + getLoadAheadPx()
    if (stillInLoadZone) {
      console.log('[InfiniteScroll] Sentinel still in load zone after load completed — loading next page')
      setIsLoadingLocked(true)
      isLoadingLockedRef.current = true
      setPage((p) => p + 1)
      setTimeout(() => {
        setIsLoadingLocked(false)
        isLoadingLockedRef.current = false
      }, 250)
    }
  }, [loading, hasMore, loadingMode, isShowingCategories, filteredPosts.length, hiddenByRating.count, hiddenByContentType.count, hiddenByGifsOnly])

  // ← / → turn the page in pagination mode, same rules as the pill's buttons.
  // Skipped while typing, with a modifier held, or with any dialog open — the
  // post viewer is a dialog and uses the arrows to move between posts itself.
  useEffect(() => {
    if (loadingMode !== 'pagination' || isShowingCategories) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      const t = e.target as HTMLElement | null
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return
      if (document.querySelector('[role="dialog"][data-state="open"]')) return

      if (e.key === 'ArrowLeft') {
        if (page === 0 || loading || isLoadingLocked) return
        setPage((p) => Math.max(0, p - 1))
      } else {
        if (loading || !hasMore || isLoadingLocked) return
        setPage((p) => p + 1)
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [loadingMode, isShowingCategories, page, loading, hasMore, isLoadingLocked])

  const blacklistedCount = allPages.length - filteredPosts.length
  const hasSankaku = sites.some(site => site.includes("sankaku"))
  const sankakuSites = sites.filter(site => site.includes("sankaku"))
  const isUnfiltered = rating === "unfiltered"

  // Handle board click (4chan only)
  const handleBoardClick = (boardId: string, boardName: string) => {
    if (containerRef.current) {
      saveScrollPosition('main', containerRef.current.scrollTop)
    }
    navigateToBoard(boardId)
  }

  // Handle post click - check if it's an album and determine navigation mode
  const handlePostClick = (post: BooruPost) => {
    console.log('╔════════════════════════════════════════════════════════════════════════════╗')
    console.log('║                        HANDLE POST CLICK - START                           ║')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('[BooruBrowser] 🖱️  Post clicked!')
    console.log('  • post.site:', post.site)
    console.log('  • post.id:', post.id)
    console.log('  • post.title:', post.title)
    console.log('  • post.type:', post.type)
    console.log('  • post.rawPost?.isCategory:', post.rawPost?.isCategory)
    console.log('  • post.rawPost?.galleryUrl:', post.rawPost?.galleryUrl)
    console.log('')
    console.log('[BooruBrowser] 📍 Current navigation state:')
    console.log('  • currentView:', currentView)
    console.log('  • currentAlbum:', currentAlbum?.title, currentAlbum?.id)
    console.log('  • currentBoard:', currentBoard)
    console.log('')
    
    // For Hitomi, Doujins, and XlecX, check if this is an actual album or just an image
    if (post.site === 'hitomi' || post.site === 'doujins' || post.site === 'xlecx') {
      console.log('[BooruBrowser] 🔍 Hitomi/Doujins/XlecX post detected')
      
      // If post type is 'image', it's a gallery page - open modal
      if (post.type === 'image') {
        console.log('[BooruBrowser] ✅ Image type detected - opening modal')
        console.log('╚════════════════════════════════════════════════════════════════════════════╝')
        console.log('')
        onPostClick?.(post)
        return
      }
      
      // If post type is 'album', navigate to album view
      if (post.type === 'album') {
        console.log('[BooruBrowser] ✅ Album type detected - navigating to album view')
        
        // Save current scroll position
        if (containerRef.current) {
          const key = currentView === 'album' ? 'album' : currentView === 'board' ? 'board' : 'main'
          const scrollPos = containerRef.current.scrollTop
          console.log('[BooruBrowser] 💾 Saving scroll position:')
          console.log('  • key:', key)
          console.log('  • scrollTop:', scrollPos)
          saveScrollPosition(key, scrollPos)
        }
        
        console.log('[BooruBrowser] 🚀 Calling navigateToAlbum...')
        console.log('╚════════════════════════════════════════════════════════════════════════════╝')
        console.log('')
        
        // Navigate to album grid
        navigateToAlbum(post)
        return
      }
    }
    
    console.log('[BooruBrowser] ⚠️  Not Hitomi/Doujins, checking if album...')
    
    const isAlbum = post.type === "album" || 
                    (post.albumImages && post.albumImages.length > 1) || 
                    (post.albumMediaCount && post.albumMediaCount > 1)
    
    console.log('  • isAlbum:', isAlbum)
    
    if (isAlbum && post.site) {
      const navMode = getNavigationMode(post, post.site)
      console.log('  • navMode:', navMode)
      
      if (navMode === 'nested') {
        console.log('[BooruBrowser] ✅ Nested navigation mode - navigating to album view')
        
        // Save current scroll position
        if (containerRef.current) {
          const key = currentView === 'album' ? 'album' : currentView === 'board' ? 'board' : 'main'
          const scrollPos = containerRef.current.scrollTop
          console.log('[BooruBrowser] 💾 Saving scroll position:')
          console.log('  • key:', key)
          console.log('  • scrollTop:', scrollPos)
          saveScrollPosition(key, scrollPos)
        }
        
        console.log('[BooruBrowser] 🚀 Calling navigateToAlbum...')
        console.log('╚════════════════════════════════════════════════════════════════════════════╝')
        console.log('')
        
        // Navigate to album grid
        navigateToAlbum(post)
        return
      }
    }
    
    // Default behavior - pass to parent handler (opens modal)
    console.log('[BooruBrowser] ℹ️  Default behavior - opening modal')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('')
    onPostClick?.(post)
  }

  // Handle back navigation
  const handleBackFromView = () => {
    console.log('╔════════════════════════════════════════════════════════════════════════════╗')
    console.log('║                      HANDLE BACK FROM VIEW - START                         ║')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('[BooruBrowser] 🔙 Back button clicked')
    
    // Get the navigation stack to determine where we're going back to
    const { navigationStack } = useNavigationStore.getState()
    const previousEntry = navigationStack[navigationStack.length - 1]
    
    console.log('[BooruBrowser] 📚 Navigation stack state:')
    console.log('  • Stack length:', navigationStack.length)
    console.log('  • Previous entry type:', previousEntry?.type)
    console.log('  • Previous entry album:', previousEntry?.album?.title)
    console.log('  • Previous entry category:', previousEntry?.categoryName)
    console.log('')
    
    // Determine the key for scroll position restoration based on where we're going
    let scrollKey = 'main'
    if (previousEntry?.type === 'album') {
      scrollKey = 'album'
      console.log('[BooruBrowser] 📍 Going back to album view')
    } else if (previousEntry?.type === 'board') {
      scrollKey = 'board'
      console.log('[BooruBrowser] 📍 Going back to board view')
    } else {
      console.log('[BooruBrowser] 📍 Going back to main view')
    }
    
    console.log('[BooruBrowser] 🚀 Calling navigateBack()...')
    console.log('╚════════════════════════════════════════════════════════════════════════════╝')
    console.log('')
    
    navigateBack()
    
    // Restore scroll position after a short delay to ensure DOM is ready
    setTimeout(() => {
      if (containerRef.current) {
        const savedPosition = getScrollPosition(scrollKey)
        console.log('[BooruBrowser] 📜 Restoring scroll position:')
        console.log('  • key:', scrollKey)
        console.log('  • position:', savedPosition)
        containerRef.current.scrollTop = savedPosition
      }
    }, 50)
  }

  // Listen for browser back button
  useEffect(() => {
    const handlePopState = () => {
      console.log('╔════════════════════════════════════════════════════════════════════════════╗')
      console.log('║                    BROWSER BACK BUTTON (popstate)                          ║')
      console.log('╚════════════════════════════════════════════════════════════════════════════╝')
      console.log('[BooruBrowser] 🌐 Browser back button pressed (popstate event)')
      console.log('  • currentView:', currentView)
      console.log('')
      
      if (currentView === 'album' || currentView === 'board') {
        console.log('[BooruBrowser] ✅ In album/board view - handling navigation')
        
        // Don't update history when triggered by browser back button (prevents double navigation)
        const { navigationStack } = useNavigationStore.getState()
        const previousEntry = navigationStack[navigationStack.length - 1]
        
        console.log('[BooruBrowser] 📚 Navigation stack state:')
        console.log('  • Stack length:', navigationStack.length)
        console.log('  • Previous entry type:', previousEntry?.type)
        console.log('  • Previous entry album:', previousEntry?.album?.title)
        console.log('  • Previous entry category:', previousEntry?.categoryName)
        console.log('')
        
        // Determine the key for scroll position restoration based on where we're going
        let scrollKey = 'main'
        if (previousEntry?.type === 'album') {
          scrollKey = 'album'
          console.log('[BooruBrowser] 📍 Going back to album view')
        } else if (previousEntry?.type === 'board') {
          scrollKey = 'board'
          console.log('[BooruBrowser] 📍 Going back to board view')
        } else {
          console.log('[BooruBrowser] 📍 Going back to main view')
        }
        
        console.log('[BooruBrowser] 🚀 Calling navigateBack(false) - NOT updating browser history')
        console.log('╚════════════════════════════════════════════════════════════════════════════╝')
        console.log('')
        
        navigateBack(false) // Pass false to prevent calling window.history.back() again
        
        // Restore scroll position after a short delay to ensure DOM is ready
        setTimeout(() => {
          if (containerRef.current) {
            const savedPosition = getScrollPosition(scrollKey)
            console.log('[BooruBrowser] 📜 Restoring scroll position:')
            console.log('  • key:', scrollKey)
            console.log('  • position:', savedPosition)
            containerRef.current.scrollTop = savedPosition
          }
        }, 50)
      } else {
        console.log('[BooruBrowser] ⏭️  Not in album/board view - ignoring popstate')
        console.log('╚════════════════════════════════════════════════════════════════════════════╝')
        console.log('')
      }
    }
    
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [currentView])

  // If viewing a board, render BoardGridView
  if (currentView === 'board' && currentBoard && is4chanOnly) {
    const source = getSource("4chan")
    const board = source?.boards?.available.find(b => b.id === currentBoard)
    const boardName = board?.name || `/${currentBoard}/`
    
    return (
      <BoardGridView
        boardId={currentBoard}
        boardName={boardName}
        onBack={handleBackFromView}
        onPostClick={handlePostClick}
      />
    )
  }

  // If viewing an album, render AlbumGridView
  if (currentView === 'album' && currentAlbum) {
    // Get category name from navigation stack if available
    const { navigationStack } = useNavigationStore.getState()
    let categoryName: string | undefined
    
    // Check if we're in a nested album view (gallery within a category)
    // The category name would be in the most recent navigation entry
    if (navigationStack.length > 0) {
      const lastEntry = navigationStack[navigationStack.length - 1]
      if (lastEntry.categoryName) {
        // Format category name for display (capitalize first letter)
        categoryName = lastEntry.categoryName.charAt(0).toUpperCase() + lastEntry.categoryName.slice(1)
      }
    }
    
    return (
      <AlbumGridView
        album={currentAlbum}
        categoryName={categoryName}
        onBack={handleBackFromView}
        onPostClick={handlePostClick}
      />
    )
  }
  
  // If browsing 4chan only, show board cards
  if (is4chanOnly && currentView === 'main') {
    return (
      <div ref={containerRef} className="flex flex-col overflow-y-auto scrollbar-hide">
        <BoardCards onBoardClick={handleBoardClick} />
      </div>
    )
  }

  if (sites.length === 0) {
    return <WelcomeSourcePicker />
  }

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription className="flex flex-col gap-3">
            <p>{t("failedToLoadPostsPleaseTryAgain")}</p>
            <Button onClick={() => window.location.reload()} variant="outline" size="sm" className="w-fit">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t("retry")}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  if (loading && allPages.length === 0) {
    return (
      <div>
        <SlowSearchNotice message={t("searchingNotice")} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 p-4">
          {Array.from({ length: postsPerPage }).map((_, i) => (
            <Card key={i} className="overflow-hidden">
              <Skeleton className="aspect-square w-full" />
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    // No `overflow-y-auto` on this root: it has no height constraint, so it grows to
    // fit its content and never actually scrolls (scrollHeight === clientHeight) — the
    // real scroller is the page container in app/page.tsx. The only thing the overflow
    // did was capture the sticky containing block, which left SourceFilterBar unable to
    // stick to anything and scrolling away with the grid.
    <div ref={containerRef} className="flex flex-col relative">
      {/* Pull-to-refresh disabled - use refresh button in UI instead */}
      
      {shouldUseInfinitePages ? (
        <InfinitePagesViewer
          sites={sites}
          tags={tags}
          negativeTags={negativeTags}
          rating={rating}
          onPostClick={handlePostClick}
          onTagClick={onTagClick}
          autoEnabled={sites.includes('xlecx') && !infinitePagesMode}
        />
      ) : (
        <>
          {/* Every active source's own filters in one labelled container — see
              SourceFilterBar for why these aren't rendered as independent siblings. */}
          <SourceFilterBar
            groups={[
              ...(hasAudioChan
                ? [{
                    id: "audiochan",
                    label: "AudioChan",
                    content: (
                      <AudioChanFilterStrip
                        filters={audioChanFilters}
                        onChange={setAudioChanFilters}
                        inline
                      />
                    ),
                  }]
                : []),
              ...(sites.includes("erome")
                ? [{ id: "erome", label: "Erome", content: <EromeVersionTabs /> }]
                : []),
              ...(sites.includes("4chan")
                ? [{ id: "4chan", label: "4chan", content: <FourchanBoardTabs /> }]
                : []),
              ...(hasRule34
                ? [{
                    id: "rule34",
                    label: "Rule34",
                    content: (
                      <Rule34FilterTabs
                        scoreMin={rule34ScoreMin}
                        onScoreChange={setRule34ScoreMin}
                        days={rule34Days}
                        onDaysChange={setRule34Days}
                      />
                    ),
                  }]
                : []),
              ...(hasE621
                ? [{
                    id: "e621",
                    label: e621StripLabel(sites),
                    content: <E621FilterTabs value={e621Order} onChange={setE621Order} />,
                  }]
                : []),
              ...(hasBestOf
                ? [{
                    id: "best-of",
                    label: bestOfSites.map((site) => site.charAt(0).toUpperCase() + site.slice(1)).join(" / "),
                    content: <YandereFilterTabs value={bestOf} onChange={setBestOf} />,
                  }]
                : []),
              ...(hasYandere
                ? [{
                    id: "yandere",
                    label: "Yande.re",
                    content: <YandereFilterTabs value={yandereOrder} onChange={setYandereOrder} />,
                  }]
                : []),
              ...(hasSankakuStrip
                ? [{
                    id: "sankaku",
                    label: "Sankaku",
                    content: <SankakuFilterTabs type={sankakuType} order={sankakuOrder} onTypeChange={setSankakuType} onOrderChange={setSankakuOrder} />,
                  }]
                : []),
              ...(hasVrhump
                ? [{
                    id: "vrhump",
                    label: "VR Hump",
                    content: <VrhumpFilterTabs category={vrhumpCategory} onCategoryChange={setVrhumpCategory} />,
                  }]
                : []),
              ...(hasSexvr
                ? [{
                    id: "sexvr",
                    label: "SexVR",
                    content: <SexvrFilterTabs channel={sexvrChannel} order={sexvrOrder} onChannelChange={setSexvrChannel} onOrderChange={setSexvrOrder} />,
                  }]
                : []),
              ...(hasRealbooru
                ? [{
                    id: "realbooru",
                    label: "Realbooru",
                    content: <RealbooruFilterTabs type={realbooruType} onTypeChange={setRealbooruType} />,
                  }]
                : []),
              ...(hasRedgifs
                ? [{
                    id: "redgifs",
                    label: "Redgifs",
                    content: <RedgifsFilterTabs type={redgifsType} order={redgifsOrder} onTypeChange={setRedgifsType} onOrderChange={setRedgifsOrder} />,
                  }]
                : []),
              ...(hasArfreaks
                ? [{
                    id: "arfreaks",
                    label: "ARFreaks",
                    content: <ArfreaksFilterTabs category={arfreaksCategory} onCategoryChange={setArfreaksCategory} />,
                  }]
                : []),
              ...(hasVrpornbase
                ? [{
                    id: "vrpornbase",
                    label: "VR Porn Base",
                    content: <VrpornbaseFilterTabs studio={vrpornbaseStudio} onStudioChange={setVrpornbaseStudio} />,
                  }]
                : []),
              ...(hasXvideos
                ? [{
                    id: "xvideos",
                    label: "XVideos",
                    content: (
                      <XvideosFilterTabs
                        order={xvideosOrder}
                        orientation={xvideosOrientation}
                        category={xvideosCategory}
                        onCategoryChange={setXvideosCategory}
                        onOrderChange={setXvideosOrder}
                        onOrientationChange={setXvideosOrientation}
                      />
                    ),
                  }]
                : []),
              ...(hasEporner
                ? [{
                    id: "eporner-vr",
                    label: "EpornerVR",
                    content: (
                      <EpornerFilterTabs
                        order={epornerOrder}
                        orientation={epornerOrientation}
                        category={epornerCategory}
                        onCategoryChange={setEpornerCategory}
                        onOrderChange={setEpornerOrder}
                        onOrientationChange={setEpornerOrientation}
                      />
                    ),
                  }]
                : []),
              ...(hasCivitai
                ? [{
                    id: "civitai",
                    label: "Civitai",
                    content: <CivitaiMediaTypeTabs value={civitaiMediaType} onChange={setCivitaiMediaType} />,
                  }]
                : []),
              ...(sites.includes("hentaiera")
                ? [{
                    id: "hentaiera",
                    label: "HentaiEra",
                    content: (
                      <HentaieraCategoryTabs
                        value={hentaieraCategory}
                        onChange={setHentaieraCategory}
                      />
                    ),
                  }]
                : []),
              ...(sites.includes("goontai")
                ? [{
                    id: "goontai",
                    label: "Goontai",
                    content: <GoontaiGenreTabs value={goontaiGenre} onChange={setGoontaiGenre} />,
                  }]
                : []),
              ...(sites.includes("hitomi")
                ? [{
                    id: "hitomi",
                    label: "Hitomi",
                    content: (
                      <HitomiFilterTabs
                        language={hitomiLanguage}
                        type={hitomiType}
                        onLanguageChange={setHitomiLanguage}
                        onTypeChange={setHitomiType}
                      />
                    ),
                  }]
                : []),
              ...(hasLewdvault
                ? [{
                    id: "lewdvault",
                    label: "LewdVault",
                    content: (
                      <LewdvaultSortTabs
                        value={lewdvaultSort}
                        onChange={setLewdvaultSort}
                      />
                    ),
                  }]
                : []),
              ...(hasDoujins
                ? [{
                    id: "doujins",
                    label: "Doujins.com",
                    content: (
                      <DoujinsCategoryTabs
                        value={doujinsCategory}
                        onChange={setDoujinsCategory}
                      />
                    ),
                  }]
                : []),
            ]}
          />

          <div className="px-2">
            <BooruGrid
              posts={audioFilteredPosts}
              onPostClick={handlePostClick}
              onTagClick={onTagClick}
              scrollContainerRef={containerRef}
              infiniteScrollBatchBoundaries={loadingMode === "infinite-scroll" ? batchBoundaries : undefined}
            />
            {/* Once per page, below the grid — not threaded into
                BooruGrid's own virtualized per-post ad cadence, since this
                only ever shows once, not on a repeating interval. */}
            {audioFilteredPosts.length > 0 && <RecommendationWidgetAd />}
          </div>

          {filteredPosts.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-4 text-center max-w-md mx-auto space-y-4">
          <p className="text-muted-foreground mb-2">{t("noPostsFound")}</p>

          {hiddenByGifsOnly > 0 && onGifsOnlyChange && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>{t("gifsOnlyHiddenTitle", { count: hiddenByGifsOnly })}</AlertTitle>
              <AlertDescription className="flex flex-col items-center space-y-3 text-center">
                <p className="text-sm">{t("gifsOnlyHiddenBody")}</p>
                <Button size="sm" onClick={() => onGifsOnlyChange(false)}>
                  {t("gifsOnlyHiddenShow")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {hiddenByContentType.count > 0 && onContentTypeVisibilityChange && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>{t("contentHiddenTitle", { count: hiddenByContentType.count })}</AlertTitle>
              <AlertDescription className="flex flex-col items-center space-y-3 text-center">
                <p className="text-sm">{t("contentHiddenBody", { types: hiddenByContentType.types.map((type) => t(`contentType_${type}`)).join(", ") })}</p>
                <Button
                  size="sm"
                  onClick={() => onContentTypeVisibilityChange({ ...contentTypeVisibility, ...Object.fromEntries(hiddenByContentType.types.map((type) => [type, true])) })}
                >
                  {t("contentHiddenShow")}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {hiddenByRating.count > 0 && activeRatings && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>{t("ratingHiddenTitle", { count: hiddenByRating.count })}</AlertTitle>
              <AlertDescription className="flex flex-col items-center space-y-3 text-center">
                <p className="text-sm">{t("ratingHiddenBody")}</p>
                <Button
                  size="sm"
                  onClick={() => setActiveRatings(ALL_FILTER_RATINGS.filter((rating) => activeRatings.includes(rating) || hiddenByRating.ratings.includes(rating)))}
                >
                  {t("ratingHiddenShow")}
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Cloudflare Verification Prompt */}
          {(() => {
            const { isVerified, wasBlocked } = useCloudflareVerificationStore.getState()
            const blockedSources = sites.filter(site => 
              !isVerified(site) && wasBlocked(site)
            )
            
            if (blockedSources.length > 0) {
              return (
                <CloudflareVerificationPrompt
                  blockedSources={blockedSources}
                  onVerified={() => {
                    // Retry search after verification
                    console.log('[BooruBrowser] Source verified, retrying search')
                    // The search will automatically retry on next render
                  }}
                  className="w-full"
                />
              )
            }
            return null
          })()}
          
          {/* Check if any sites require auth or if we should show a different message */}
          {sites.length > 0 && hiddenByRating.count === 0 && (() => {
            const sitesRequiringAuth = sites.filter(site => {
              const config = getSource(site)
              return config?.requiresAuth !== false
            })
            
            const noAuthSites = sites.filter(site => {
              const config = getSource(site)
              return config?.requiresAuth === false
            })
            
            // If only no-auth sites (like rule34.xyz), show feedback message
            if (sitesRequiringAuth.length === 0 && noAuthSites.length > 0) {
              return (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>{t("sourceMayBeUnavailable")}</AlertTitle>
                  <AlertDescription className="space-y-3">
                    <p className="text-sm">
                      {t("theSelectedSourceSMayBeTemporarilyUnavai")}
                    </p>
                    <p className="text-sm">
                      {t("pleaseTrySelectingADifferentSourceOrSend")}
                    </p>
                  </AlertDescription>
                </Alert>
              )
            }
            
            // Otherwise show auth prompt for sites that support it
            return (
              <div className="flex flex-col items-center gap-3 text-center py-2">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-foreground" />
                  <span className="font-semibold text-sm">{t("needAPICredentials")}</span>
                </div>
                <p className="text-sm text-muted-foreground max-w-xs">
                  {t("someBoorusRequireAuthenticationOrHaveBette")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAuthDialogSource(sitesRequiringAuth[0])
                    setAuthDialogOpen(true)
                  }}
                >
                  <Key className="h-4 w-4 mr-2" />
                  {t("addAPICredentials")}
                </Button>
              </div>
            )
          })()}
        </div>
      )}

      {/* Both branches now use the isShowingCategories memo rather than
          recomputing `filteredPosts.every(...)` inline. The inline form was a
          leftover from an incomplete hoist, and it was subtly wrong in a way
          the memo already guards against: `[].every()` is vacuously TRUE, so
          an empty result set counted as "all categories" and suppressed both
          the sentinel (breaking infinite scroll on first paint, before any
          posts arrive) and the pagination pill (leaving no way back after
          paging past the last page). */}
      {loadingMode === 'infinite-scroll' && !isShowingCategories ? (
        <div ref={setObserverTarget} className="h-20 flex items-center justify-center">
          {(loading || isLoadingLocked) && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
        </div>
      ) : loadingMode === 'pagination' && !isShowingCategories ? (
        // Sticky footer pill — floats independent of whatever the grid is
        // rendering above it (single images, albums, doujin strips all vary
        // in height/layout, so a pill anchored to the scroll container's own
        // bottom edge never collides with per-post content the way a strip
        // fused to the header would). Dims while scrolling, comes back to
        // full opacity once motion settles.
        <div className="sticky bottom-4 z-20 flex justify-center pointer-events-none pb-[env(safe-area-inset-bottom)]">
          <div
            className={`pointer-events-auto flex items-center gap-1 rounded-full border border-white/10 bg-background/70 backdrop-blur-md shadow-lg px-2 py-2 transition-opacity duration-300 ${
              isPaginationPillIdle ? "opacity-100" : "opacity-40"
            }`}
          >
            <Button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading || isLoadingLocked}
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full"
              aria-label={t("previousPage")}
              title={`${t("previousPage")} (←)`}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[3.5rem] text-center text-sm font-medium tabular-nums px-1">
              {(loading || isLoadingLocked) ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                `Page ${page + 1}`
              )}
            </span>
            <Button
              onClick={() => setPage((p) => p + 1)}
              disabled={loading || !hasMore || isLoadingLocked}
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full"
              aria-label={t("nextPage")}
              title={`${t("nextPage")} (→)`}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
        </>
      )}

      {/* Auth Dialog */}
      {authDialogSource && (
        <BooruAuthDialog
          open={authDialogOpen}
          onOpenChange={setAuthDialogOpen}
          source={getSource(authDialogSource)!}
          onSave={async () => {
            // Refresh the search after saving credentials
            setAllPages([])
            setBatchBoundaries([])
            setPage(0)
          }}
        />
      )}
    </div>
  )
}
